import { useEffect, useState, useCallback, useMemo } from "react";
import { getMarkerApi } from "../api/carModelApi";
import { IMarkerItem } from "../types";

/**
 * Custom hook to fetch and manage the list of car makers.
 * @returns {object} - An object containing:
 *   - `makerList` {IMarkerItem[]} - The list of car makers.
 *   - `loading` {boolean} - The loading state indicating if the data is being fetched.
 *   - `error` {boolean} - Indicates if there was an error during fetching.
 *   - `refetch` {() => Promise<void>} - Function to manually refetch the data.
 */
export default function useMaker() {
  const [makerList, setMakerList] = useState<IMarkerItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const getMakerList = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const data = await getMarkerApi();
      if (!data) {
        setMakerList([]);
        return;
      }
      setMakerList(data.list || []);
    } catch (err) {
      setError(true);
      setMakerList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getMakerList();
  }, [getMakerList]);

  // Provide memoized result to prevent unnecessary re-renders
  return useMemo(() => ({
    makerList,
    loading,
    error,
    refetch: getMakerList
  }), [makerList, loading, error, getMakerList]);
}
